/**
 * Host-only Top-3 party starters.
 *
 * Browser path: httpsCallable(recommendPartyStarters) only.
 * Candidate discovery (collection query of recordingRecommendationProfiles)
 * runs inside the Cloud Function via FirestoreCandidateSource / Admin SDK.
 * The browser never list/queries that collection for Top-3.
 *
 * Never falls back to public YouTube search.
 */

import { httpsCallable } from 'firebase/functions';
import { ensureAnonymousAuth, functions } from '../firebase';
import { describeError } from '../utils/errorLog';
import { parseYoutubeProviderAssetKey, youtubeMqThumbnailUrl } from '../catalog/youtubeProviderAsset';

export const RECOMMEND_PARTY_STARTERS_CALLABLE = 'recommendPartyStarters';
export const RECOMMEND_NEXT_TRACK_CALLABLE = 'recommendNextTrack';

export function starterQueueTrack(row) {
  if (!row?.recordingId || !row?.providerAssetKey || row.playable === false) return null;
  const parsed = parseYoutubeProviderAssetKey(row.providerAssetKey);
  if (!parsed?.videoId) return null;
  const title = typeof row.displayTitle === 'string' && row.displayTitle.trim()
    ? row.displayTitle.trim()
    : 'Recommended track';
  const artist = typeof row.displayArtist === 'string' ? row.displayArtist.trim() : '';
  const thumbnail = typeof row.artworkUrl === 'string' && row.artworkUrl.trim()
    ? row.artworkUrl.trim()
    : youtubeMqThumbnailUrl(parsed.videoId);
  return {
    id: parsed.videoId,
    videoId: parsed.videoId,
    title,
    artist,
    thumbnail,
    source: 'party-starter',
    recordingId: row.recordingId,
    providerAssetKey: row.providerAssetKey,
  };
}

export function normalizeStarterResults(payload) {
  const data = payload && typeof payload === 'object' ? payload : {};
  if (data.status && data.status !== 'SUCCESS') {
    return {
      status: data.status,
      reason: data.reason || null,
      tracks: [],
    };
  }
  const topK = Array.isArray(data.topK) ? data.topK : [];
  const seen = new Set();
  const tracks = [];
  topK.forEach((row) => {
    if (tracks.length >= 3) return;
    if (!row?.playable || !row.recordingId || seen.has(row.recordingId)) return;
    const track = starterQueueTrack(row);
    if (!track) return;
    seen.add(row.recordingId);
    tracks.push({
      ...track,
      finalScore: row.finalScore,
    });
  });
  return {
    status: data.status || 'NO_RECOMMENDATION',
    reason: data.reason || null,
    tracks,
  };
}

export async function fetchNextPlayable(eventId, { excludeRecordingIds = [], callable } = {}) {
  if (!eventId) return null;
  try {
    await ensureAnonymousAuth();
    const invoke = callable || httpsCallable(functions, RECOMMEND_NEXT_TRACK_CALLABLE);
    const response = await invoke({
      eventId,
      continuation: true,
      excludeRecordingIds: excludeRecordingIds.filter((id) => typeof id === 'string' && id.startsWith('rhy_rec_')),
    });
    const normalized = normalizeStarterResults(response?.data);
    return normalized.tracks[0] || null;
  } catch (error) {
    console.error('Next playable track failed:', describeError(error));
    return null;
  }
}

export async function fetchPartyStarters(eventId, { callable } = {}) {
  if (!eventId) return { ok: false, reason: 'missing-event', tracks: [] };

  try {
    await ensureAnonymousAuth();
  } catch (error) {
    console.error('Party starters auth failed:', describeError(error));
    return { ok: false, reason: 'unavailable', tracks: [] };
  }

  const invoke = callable || httpsCallable(functions, RECOMMEND_PARTY_STARTERS_CALLABLE);
  try {
    const response = await invoke({ eventId });
    const normalized = normalizeStarterResults(response?.data);
    return { ok: true, ...normalized };
  } catch (error) {
    console.error('Party starters failed:', describeError(error));
    return { ok: false, reason: 'unavailable', tracks: [] };
  }
}
